import { Button } from "$components/button/button";
import { useAnalytics } from "$global-providers/analytics-provider/provider";
import {
  type Territory,
  useConfig,
} from "$global-providers/config-provider/provider";
import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import {
  type Patient,
  useRxCollectionPatient,
} from "$global-providers/rxdb-provider/collections/patient";
import { useServerTime } from "$global-providers/server-time-provider/provider";
import { toast } from "$global-providers/toast-provider";
import { type UserContextUser, useUser } from "$global-providers/user-provider";
import {
  COUNTRY_CODES,
  NEXT_OF_KIN_RELATIONSHIP_CODES,
  TERRITORY,
} from "$routes/_lib/constants";
import { randomId } from "$routes/_lib/random-id";
import { NewFooter } from "$routes/new/_lib/NewFooter";
import NewTitle from "$routes/new/_lib/NewTitle";
import {
  linkToNewCardsActivateCard,
  linkToNewPatientDuplicates,
  linkToNewPatientStep1,
  linkToNewPatientStep2V2,
  linkToNewPatientStep3,
} from "$routes/new/links";
import { LinkAccountModal } from "$routes/patient/_lib/link-account-modal";
import { isChildPatient } from "$routes/patient/[:patientId]/_lib/is-child-patient";
import { gql } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Sentry from "@sentry/react";
import { format } from "date-fns";
import {
  ContactPointOwnership,
  ContactPointSystem,
  type Gender,
  GuardianRelationship,
  MaritalStatus,
  NationalIdType,
} from "elephant-web/gql";
import React, { useEffect, useState } from "react";
import {
  type DefaultValues,
  FormProvider,
  type UseFormReturn,
  useForm,
} from "react-hook-form";
import { defineMessage, FormattedMessage, useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { mapGuardianToPatientGuardian } from "../../../patient/_lib/guardian-mapper";
import { useRegistrationFormState } from "../_lib/patient-registration-context";
import { GuardianSection } from "../_lib/sections/guardian-section";
import {
  PhoneOwnershipRadioOptions,
  phoneOwnershipToContactPointOwnerMap,
} from "../_lib/sections/phone-ownership";
import {
  addressLine,
  phoneNumberValidationMessage,
  validatePhoneNumber,
} from "../_lib/sharedFormValidation";
import { ConsentRHFModal } from "../step1/_lib/ConsentRHFModal";
import { NewPatientStep1FormValues, RegistrationSteps } from "../step1/page";
import {
  type SearchPatientsForLinkingQuery,
  useSearchPatientsForLinkingLazyQuery,
  useSearchPatientsForRegistrationLazyQuery,
} from "./__generated__/page.gql";
import { AdditionalInformationSection } from "./_lib/AdditionalInformationSection";
import { ContactSection } from "./_lib/ContactSection";
import { GuardianConsentModal } from "./_lib/GuardianConsentModal";
import { NextOfKinSection } from "./_lib/NextOfKinSection";
import {
  getNationalIdNumber,
  getNationalIdOwner,
  getNationalIdValidationSchema,
} from "./_lib/national-id-schema";

export type MatchedPatient = {
  uuid: string;
  cardId?: string;
  fullName: string;
  phone?: string;
  address: string;
};

export const guardianConsentsValidationSchema = Yup.object({
  healthcareComms: Yup.boolean().required("Required"),
  marketingComms: Yup.boolean().required("Required"),
});

export const getGuardianValidationSchema = (
  territory: Territory,
  isChild: boolean,
) => ({
  guardian: isChild
    ? Yup.object({
        fullName: Yup.string().when("patientId", {
          is: (patientId) => !patientId,
          then: (schema) => schema.required(),
          otherwise: (schema) => schema.optional().nullable(),
        }),
        phone: Yup.mixed().when("patientId", {
          is: (patientId) => !patientId,
          then: Yup.object({
            ownership: Yup.string()
              .oneOf(Object.values(PhoneOwnershipRadioOptions))
              .required(),
            number: Yup.mixed().when("ownership", {
              is: (phoneOwnership: ContactPointOwnership) =>
                phoneOwnership &&
                (phoneOwnership.includes(PhoneOwnershipRadioOptions.Personal) ||
                  phoneOwnership.includes(PhoneOwnershipRadioOptions.Shared)),
              then: Yup.string().test(
                "phone number validation",
                phoneNumberValidationMessage(territory),
                (phoneNumber) => validatePhoneNumber(phoneNumber, territory),
              ),
              otherwise: Yup.string().nullable(),
            }),
          }),
          otherwise: Yup.mixed().nullable().optional(),
        }),
        relationship: Yup.string().oneOf(Object.values(GuardianRelationship)),
        // Only require consents on Step 2 for Pakistan (other territories collect on Step 3)
        ...(territory === TERRITORY.PAKISTAN
          ? { consents: guardianConsentsValidationSchema }
          : {}),
        patientId: Yup.string().nullable().optional(),
      })
    : Yup.object().nullable(),
});
export const kenyaEducationValidationSchema = {
  education: Yup.object({
    isStudent: Yup.bool().nullable(),
    school: Yup.string().when("isStudent", {
      is: true,
      then: Yup.string(),
      otherwise: Yup.string().nullable(),
    }),
    nemisNumber: Yup.string().when("isStudent", {
      is: true,
      then: Yup.string().matches(/^[a-zA-Z0-9]{6}$/, {
        // https://github.com/jquense/yup/issues/822 => hack message obj
        message: defineMessage({
          id: "patient.form.validation.nemisNumber",
          defaultMessage: "NEMIS number must contain 6 alphanumeric characters",
        }),
      }),
      otherwise: Yup.string().nullable(),
    }),
  }).nullable(),
};

export const getNewPatientStep2ValidationSchema = (
  territory: Territory,
  registrationReformatFlag: boolean,
  isChild: boolean,
) =>
  Yup.object({
    nationality: Yup.string()
      .oneOf(Object.values(COUNTRY_CODES), "Please select a nationality")
      .required(),
    phoneOwnership: Yup.mixed<PhoneOwnershipRadioOptions>()
      .oneOf(Object.values(PhoneOwnershipRadioOptions))
      .required(),
    phoneNumber: Yup.string().when("phoneOwnership", {
      is: (phoneOwnership: PhoneOwnershipRadioOptions) =>
        phoneOwnership === PhoneOwnershipRadioOptions.Personal ||
        phoneOwnership === PhoneOwnershipRadioOptions.Shared,
      then: Yup.string().test(
        "phone number validation",
        phoneNumberValidationMessage(territory),
        (phoneNumber) => validatePhoneNumber(phoneNumber, territory),
      ),
      otherwise: Yup.string().nullable(),
    }),
    maritalStatus: Yup.mixed<MaritalStatus>()
      .oneOf(Object.values(MaritalStatus), "Please select a marital status")
      .required(),
    email: Yup.string().email().nullable(),
    ...(isChild && registrationReformatFlag
      ? {}
      : {
          nationalId: getNationalIdValidationSchema(territory),
          nextOfKin: Yup.object({
            fullName: Yup.string(),
            phoneNumber: Yup.string().when("fullName", {
              is: (fullName) => !!fullName,
              then: Yup.string()
                .test(
                  "phone number validation",
                  phoneNumberValidationMessage(territory),
                  (phoneNumber) => validatePhoneNumber(phoneNumber, territory),
                )
                .required(),
            }),
            relationship: Yup.string().when("fullName", {
              is: (fullName) => !!fullName,
              then: Yup.string()
                .oneOf(Object.values(NEXT_OF_KIN_RELATIONSHIP_CODES))
                .required(),
            }),
            address: Yup.object({
              country: Yup.string(),
              line1: addressLine(),
              line2: addressLine(),
              line3: addressLine(),
              line4: addressLine(),
            }),
          }).required(),
        }),
    ...(registrationReformatFlag
      ? getGuardianValidationSchema(territory, isChild)
      : {}),
    ...(territory === TERRITORY.KENYA ? kenyaEducationValidationSchema : {}),
    ...(territory === TERRITORY.PAKISTAN
      ? {
          consent: Yup.object({
            healthcareComms: Yup.boolean().required("Required"),
            elephantUseOfAnonData: Yup.boolean().required("Required"),
            productMarketing: Yup.boolean().required("Required"),
          }),
        }
      : {}),
  });

export type NewPatientStep2FormValues = Yup.InferType<
  ReturnType<typeof getNewPatientStep2ValidationSchema>
>;

const getDefaultValues = ({
  step2FormValues,
  isChild,
  territory,
}: {
  step2FormValues: NewPatientStep2FormValues;
  isChild: boolean;
  territory: Territory;
}): DefaultValues<NewPatientStep2FormValues> => {
  return {
    nationality: step2FormValues?.nationality ?? null,
    phoneOwnership:
      (step2FormValues?.phoneOwnership ?? isChild)
        ? PhoneOwnershipRadioOptions.None
        : PhoneOwnershipRadioOptions.Personal,
    phoneNumber: step2FormValues?.phoneNumber ?? null,
    maritalStatus: step2FormValues?.maritalStatus ?? MaritalStatus.Unk,
    nationalId: step2FormValues?.nationalId ?? {
      number: "",
      type:
        territory === TERRITORY.PAKISTAN ? NationalIdType.Primary : undefined,
      owner: undefined,
    },
    email: step2FormValues?.email ?? null,
    nextOfKin: isChild
      ? null
      : (step2FormValues?.nextOfKin ?? {
          fullName: "",
          phoneNumber: "",
          relationship: "",
          address: {
            country: "",
            line1: "",
            line2: "",
            line3: "",
            line4: "",
          },
        }),
    consent:
      territory === TERRITORY.PAKISTAN && isChild
        ? { healthcareComms: false, productMarketing: false }
        : {},
    guardian: step2FormValues?.guardian
      ? {
          phone: {
            number: step2FormValues.guardian.phone.number,
            ownership: step2FormValues.guardian.phone.ownership,
          },
          relationship: step2FormValues.guardian.relationship,
          fullName: step2FormValues.guardian.fullName,
          consents: {
            healthcareComms: step2FormValues.guardian.consents.healthcareComms,
            marketingComms: step2FormValues.guardian.consents.healthcareComms,
          },
        }
      : isChild
        ? {
            phone: {
              number: null,
              ownership: PhoneOwnershipRadioOptions.Personal,
            },
            relationship: null,
            fullName: null,
            consents: {},
          }
        : null,
  };
};

// Patient mapping function for territories that skip step 3 - creates patient directly from step 1 + step 2 data
const mapStep1AndStep2ToRxPatient = ({
  step1FormValues,
  step2FormValues,
  user,
  getCurrentTime,
  generateId,
}: {
  step1FormValues: NewPatientStep1FormValues;
  step2FormValues: NewPatientStep2FormValues;
  user: UserContextUser;
  getCurrentTime: () => Date;
  generateId: () => string;
}): Patient => ({
  address: {
    country: step1FormValues.address.country,
    line1: step1FormValues.address.line1,
    line2: step1FormValues.address.line2,
    line3: step1FormValues.address.line3 ?? null,
    line4: step1FormValues.address.line4,
  },
  consent: {
    elephantUseOfAnonData: step2FormValues.consent.elephantUseOfAnonData,
    productMarketing: step2FormValues.consent.productMarketing,
    accessToHealthRecords: false,
    territoryUseOfData: false,
    healthcareComms: step2FormValues.consent.healthcareComms,
  },
  dob: format(new Date(step1FormValues.dob), "yyyy-MM-dd"),
  education: null,
  email: step2FormValues.email || null,
  facilityId: user.facilityId,
  gender: step1FormValues.gender,
  id: generateId(),
  insurance: null,
  maritalStatus:
    {
      [MaritalStatus.L]: "divorced",
      [MaritalStatus.M]: "married",
      [MaritalStatus.U]: "single",
      [MaritalStatus.W]: "widowed",
    }[step2FormValues.maritalStatus] ?? "unknown",
  meta: {
    createdAt: getCurrentTime().toISOString(),
    createdByUserId: user.id,
    updatedAt: getCurrentTime().toISOString(),
    updatedByUserId: user.id,
  },
  name: {
    first: step1FormValues.firstName,
    middle: step1FormValues.middleName ?? null,
    last: step1FormValues.lastName,
  },
  nationalId: step2FormValues.nationalId
    ? {
        owner: getNationalIdOwner(step2FormValues.nationalId),
        value: getNationalIdNumber(step2FormValues.nationalId),
      }
    : null,
  nationality: step2FormValues.nationality,
  newToFacility: step1FormValues.newToFacility,
  nextOfKin: step2FormValues.nextOfKin?.fullName
    ? {
        address: step2FormValues.nextOfKin.address
          ? {
              country: step2FormValues.nextOfKin.address.country ?? null,
              line1: step2FormValues.nextOfKin.address.line1 ?? null,
              line2: step2FormValues.nextOfKin.address.line2 ?? null,
              line3: step2FormValues.nextOfKin.address.line3 ?? null,
              line4: step2FormValues.nextOfKin.address.line4 ?? null,
            }
          : null,
        fullName: step2FormValues.nextOfKin.fullName,
        phoneNumber: step2FormValues.nextOfKin.phoneNumber ?? null,
        relationship: step2FormValues.nextOfKin.relationship,
      }
    : null,
  organisationId: user.organisationId,
  phone:
    step2FormValues.phoneOwnership === PhoneOwnershipRadioOptions.None
      ? null
      : {
          number: step2FormValues.phoneNumber,
          ownership:
            phoneOwnershipToContactPointOwnerMap[
              step2FormValues.phoneOwnership
            ],
        },
  registrationFee: null, // Pakistan skips payment (step 3)
  guardians: mapGuardianToPatientGuardian({
    guardianData: step2FormValues.guardian,
    user,
    currentTime: getCurrentTime().toISOString(),
    generateId,
  }),
});

export const Page: React.FC = () => {
  const {
    step1FormValues,
    step2FormValues,
    setStep2FormValues,
    setDuplicates,
    trackingId,
  } = useRegistrationFormState();
  const navigate = useNavigate();

  useEffect(() => {
    if (!step1FormValues) {
      navigate(linkToNewPatientStep1());
    }
  }, [navigate, step1FormValues]);
  const {
    state: { hasPassedDuplicateCheck = false },
  } = useLocation();
  const [registrationReformatFlag] = useFeatureFlag(
    featureFlags.immunisations.registrationReformat,
  );

  const isChild =
    registrationReformatFlag &&
    isChildPatient(new Date(step1FormValues?.dob ?? "")); // empty string will give us isChildPatient: false
  const { territory } = useConfig();
  const { formatMessage } = useIntl();
  const { trackEvent } = useAnalytics();
  trackEvent("Patient RegistrationStepStarted", {
    step: RegistrationSteps.Step2,
    trackingId,
    isChildPatient: isChild,
  });
  const [linkAccountModalState, setLinkAccountModalState] = useState<{
    open: boolean;
    patient?: MatchedPatient;
  }>({ open: false });

  const [marketingConsentOpen, setMarketingConsentOpen] = useState(false);
  const patientCollection = useRxCollectionPatient();
  const user = useUser();
  const { getCurrentTime } = useServerTime();

  const formMethods = useForm<NewPatientStep2FormValues>({
    defaultValues: getDefaultValues({
      isChild,
      step2FormValues,
      territory,
    }),
    resolver: yupResolver(
      getNewPatientStep2ValidationSchema(
        territory,
        registrationReformatFlag,
        isChild,
      ),
    ),
    mode: "onTouched",
  });

  const [searchPatientsForLinking, { loading: isSearchingForLinking }] =
    useSearchPatientsForLinkingLazyQuery({
      fetchPolicy: "network-only",
      onCompleted: (data: SearchPatientsForLinkingQuery) => {
        processSearchResults({
          data,
          setLinkAccountModalState,
          trackLinkingModalOpen: () =>
            trackEvent("Patient RegistrationStepStarted", {
              step: RegistrationSteps.LinkGuardian,
              trackingId,
              isChildPatient: isChild,
            }),
          continueToNextFormStep: () => {
            const formData = formMethods.getValues();
            setStep2FormValues(formData);

            navigate(linkToNewPatientStep3());
          },
        });
      },
      onError: () => {
        const formData = formMethods.getValues();
        setStep2FormValues(formData);

        navigate(linkToNewPatientStep3());
      },
    });

  useEffect(() => {
    if (!hasPassedDuplicateCheck) return;
    const tryGuardianLink = async () => {
      if (
        await checkForGuardianLink({
          vals: formMethods.getValues(),
          isChild,
          searchPatients: searchPatientsForLinking,
        })
      ) {
      }
    };

    tryGuardianLink();
  }, [
    formMethods.getValues,
    isChild,
    hasPassedDuplicateCheck,
    searchPatientsForLinking,
  ]);

  const [searchPatientsForDuplicate, { loading: isSearchingForDuplicate }] =
    useSearchPatientsForRegistrationLazyQuery({
      onError: () => {
        toast.warning("Failed to search for duplicates, continuing anyway.");
      },
    });

  const handleMarketingConsentSubmit = async (
    vals: NewPatientStep2FormValues,
  ) => {
    if (!patientCollection || !step1FormValues) {
      toast.error("Error submitting patient, refresh the page");
      Sentry.captureMessage(
        "RxDb patientCollection or step1FormValues null when submitting patient",
        "error",
      );
      return;
    }

    try {
      const patientData = mapStep1AndStep2ToRxPatient({
        step1FormValues,
        step2FormValues: vals,
        user,
        getCurrentTime,
        generateId: randomId,
      });

      const patient = await patientCollection.insert(patientData);

      // Navigate to card activation (skipping step 3)
      navigate(linkToNewCardsActivateCard(), {
        state: {
          patientId: patient.id,
          patient: {
            id: patient.id,
            name: {
              first: patient.name.first,
              middle: patient.name.middle,
              last: patient.name.last,
            },
            contactPoints: patient.phone
              ? [
                  {
                    system: ContactPointSystem.Phone,
                    value: patient.phone.number,
                    ownership: {
                      personal: ContactPointOwnership.Personal,
                      shared: ContactPointOwnership.Shared,
                    }[patient.phone.ownership],
                  },
                ]
              : [],
          },
        },
      });
    } catch (error) {
      toast.error(
        formatMessage(
          {
            id: "registerNewPatient.generalDetailsPage.submit.error",
            defaultMessage: "Failed to create patient: {error}",
          },
          { error: error.message },
        ),
      );
    }
  };

  return (
    <>
      <NewTitle pageNumber={2} />
      <FormProvider {...formMethods}>
        <form
          style={{
            width: "100%",
          }}
          name="registerPatient.step2"
          onSubmit={formMethods.handleSubmit(async (data) => {
            setStep2FormValues(data);
            try {
              const result = await searchPatientsForDuplicate({
                fetchPolicy: "network-only",
                variables: {
                  patient: {
                    firstName: step1FormValues.firstName,
                    middleName: step1FormValues.middleName,
                    lastName: step1FormValues.lastName,
                    gender: step1FormValues.gender as Gender,
                    dob: format(new Date(step1FormValues?.dob), "yyyy-MM-dd"),
                    address: {
                      country: step1FormValues.address.country,
                      state: step1FormValues.address.line1,
                      subCounty: step1FormValues.address.line2,
                      ward: step1FormValues.address.line3,
                      street: step1FormValues.address.line4,
                    },
                    phoneNumber: data.phoneNumber,
                    nationalIdNumber: getNationalIdNumber(data.nationalId),
                  },
                },
              });

              if (result.data?.searchPatients?.length > 0) {
                // Save step2 form data to context before navigating
                setStep2FormValues(data);

                // Set duplicates in context with navigation override
                setDuplicates(
                  result.data.searchPatients,
                  isChild ? linkToNewPatientStep2V2() : linkToNewPatientStep3(),
                );

                navigate(linkToNewPatientDuplicates());
                return;
              }

              // If no duplicates, proceed to guardian linking or next step
              if (
                await checkForGuardianLink({
                  vals: data,
                  isChild,
                  searchPatients: searchPatientsForLinking,
                })
              ) {
                return;
              }

              navigate(linkToNewPatientStep3());
            } catch (error) {
              toast.error(
                formatMessage(
                  {
                    id: "registerNewPatient.generalDetailsPage.submit.error",
                    defaultMessage: "Failed to create patient: {error}",
                  },
                  { error: error.message },
                ),
              );
            }
          })}
        >
          <AdditionalInformationSection
            isChildPatient={isChild}
            showNationalId={true}
            fieldNames={{
              nationality: "nationality",
              maritalStatus: "maritalStatus",
              language: "language",
              nationalId: {
                number: "nationalId.number",
                type: "nationalId.type",
                owner: "nationalId.owner",
              },
            }}
          />
          {isChild ? (
            <GuardianSection
              hasLinkedGuardian={false}
              fieldNames={{
                fullName: "guardian.fullName",
                phoneNumber: "guardian.phone.number",
                phoneOwnership: "guardian.phone.ownership",
                relationship: "guardian.relationship",
              }}
            />
          ) : (
            <>
              <ContactSection
                required
                fieldNames={{
                  phoneNumber: "phoneNumber",
                  phoneOwnership: "phoneOwnership",
                  email: "email",
                }}
              />
              <NextOfKinSection
                territory={territory}
                fieldNames={{
                  fullName: "nextOfKin.fullName",
                  phoneNumber: "nextOfKin.phoneNumber",
                  relationship: "nextOfKin.relationship",
                  address: {
                    country: "nextOfKin.address.country",
                    addressLine1: "nextOfKin.address.line1",
                    addressLine2: "nextOfKin.address.line2",
                    addressLine3: "nextOfKin.address.line3",
                    addressLine4: "nextOfKin.address.line4",
                  },
                }}
              />
            </>
          )}
          <NewFooter>
            <Button
              variant="contained"
              color="secondary"
              type={territory === TERRITORY.PAKISTAN ? "button" : "submit"}
              onClick={() => {
                if (territory === TERRITORY.PAKISTAN) {
                  const baseFields = [
                    "nationality",
                    "phoneOwnership",
                    "phoneNumber",
                    "email",
                    "maritalStatus",
                    "nationalId",
                    "nextOfKin",
                    "education",
                  ] as const;

                  const fieldsToValidate = isChild
                    ? ([
                        ...baseFields,
                        "guardian.fullName",
                        "guardian.phone.number",
                        "guardian.phone.ownership",
                        "guardian.relationship",
                      ] as const)
                    : baseFields;

                  formMethods.trigger(fieldsToValidate).then((isValid) => {
                    if (isValid) {
                      setMarketingConsentOpen(true);
                      trackEvent("Patient RegistrationStepStarted", {
                        step: RegistrationSteps.Consents,
                        isChildPatient: isChild,
                        trackingId,
                      });
                    }
                  });
                }
              }}
              loading={isSearchingForDuplicate || isSearchingForLinking}
            >
              <FormattedMessage
                id="form.page.continue"
                defaultMessage="Continue"
              />
            </Button>
          </NewFooter>
          <LinkAccountModal
            patient={linkAccountModalState.patient}
            open={linkAccountModalState.open}
            onClose={() => {
              setLinkAccountModalState({ open: false });
              const formData = formMethods.getValues();
              setStep2FormValues(formData);
              navigate(linkToNewPatientStep3());
            }}
            onLink={() => {
              handleLinkAccount({
                linkAccountModalState,
                formMethods,
                setLinkAccountModalState,
                continueFn: () => {
                  const formData = formMethods.getValues();
                  setStep2FormValues(formData);
                  navigate(linkToNewPatientStep3());
                },
              });
            }}
          />
          {/* Marketing consent modal for territories that skip step 3 */}
          {territory === TERRITORY.PAKISTAN && (
            <>
              <ConsentRHFModal
                open={marketingConsentOpen && !isChild}
                onClose={() => setMarketingConsentOpen(false)}
                onSubmit={formMethods.handleSubmit(
                  handleMarketingConsentSubmit,
                )}
                submitLoading={formMethods.formState.isSubmitting}
                fieldNames={{
                  healthcareComms: "consent.healthcareComms",
                  elephantUseOfAnonData: "consent.elephantUseOfAnonData",
                  productMarketing: "consent.productMarketing",
                }}
                dob={step1FormValues.dob.toISOString()}
              />
              <GuardianConsentModal
                open={marketingConsentOpen && isChild}
                onClose={() => setMarketingConsentOpen(false)}
                onSubmit={formMethods.handleSubmit(
                  handleMarketingConsentSubmit,
                )}
                submitLoading={formMethods.formState.isSubmitting}
                fieldNames={{
                  healthcareComms: "guardian.consents.healthcareComms",
                  marketingComms: "guardian.consents.marketingComms",
                  elephantUseOfAnonData: "consent.elephantUseOfAnonData",
                }}
              />
            </>
          )}
        </form>
      </FormProvider>
    </>
  );
};

const processSearchResults = ({
  data,
  setLinkAccountModalState,
  trackLinkingModalOpen,
  continueToNextFormStep,
}: {
  data: SearchPatientsForLinkingQuery | undefined;
  setLinkAccountModalState: (state: {
    open: boolean;
    patient?: MatchedPatient;
  }) => void;
  trackLinkingModalOpen: () => void;
  continueToNextFormStep: () => void;
}) => {
  const matchingPatients = data?.searchPatients || [];

  if (matchingPatients.length === 1) {
    // Found exactly one match, show the linking modal
    const matchedPatient = matchingPatients[0];
    const phone = matchedPatient.person?.contactPoints?.find(
      (cp) => cp.system === "phone",
    )?.value;

    const fullName = matchedPatient.person.name.fullname;

    if (!fullName || !phone) {
      continueToNextFormStep();
      return;
    }

    const addressParts = [
      matchedPatient.person?.addresses?.[0]?.street,
      matchedPatient.person?.addresses?.[0]?.subCounty,
      matchedPatient.person?.addresses?.[0]?.ward,
      matchedPatient.person?.addresses?.[0]?.state,
      matchedPatient.person?.addresses?.[0]?.country,
    ]
      .filter(Boolean)
      .join(", ");

    trackLinkingModalOpen();
    setLinkAccountModalState({
      open: true,
      patient: {
        uuid: matchedPatient.uuid,
        cardId: matchedPatient.card?.id,
        fullName,
        phone,
        address: addressParts,
      },
    });
  } else {
    continueToNextFormStep();
  }
};
const handleLinkAccount = ({
  linkAccountModalState,
  formMethods,
  setLinkAccountModalState,
  continueFn,
}: {
  linkAccountModalState: { patient?: MatchedPatient };
  formMethods: UseFormReturn<NewPatientStep2FormValues>;
  setLinkAccountModalState: (state: {
    open: boolean;
    patient?: MatchedPatient;
  }) => void;
  continueFn: () => void;
}) => {
  const linkedPatientId = linkAccountModalState.patient?.uuid;
  if (linkedPatientId) {
    // Update the form with the linked guardian UUID and clear name/phone as they'll come from the linked patient
    formMethods.setValue("guardian.patientId", linkedPatientId);
    formMethods.setValue("guardian.fullName", "");
    formMethods.setValue("guardian.phone.number", "");
  }
  setLinkAccountModalState({ open: false });
  continueFn();
};

const checkForGuardianLink = async ({
  vals,
  isChild,
  searchPatients,
}: {
  vals: NewPatientStep2FormValues;
  isChild: boolean;
  searchPatients: (options: {
    variables: {
      patient: { firstName: string; lastName: string; phoneNumber: string };
      isFuzzySearch?: boolean;
    };
  }) => void;
}): Promise<boolean> => {
  // Only search if we have a guardian with name and phone
  if (isChild && vals.guardian?.fullName && vals.guardian?.phone?.number) {
    const fullNameParts = vals.guardian.fullName.split(" ");
    const firstName = fullNameParts[0] || "";
    const lastName =
      fullNameParts.length > 1 ? fullNameParts[fullNameParts.length - 1] : "";

    searchPatients({
      variables: {
        patient: {
          firstName,
          lastName,
          phoneNumber: vals.guardian.phone.number,
        },
        isFuzzySearch: true,
      },
    });

    return true;
  }
  return false;
};

gql`
  query SearchPatientsForLinking(
    $patient: SearchPatientInput!
    $isFuzzySearch: Boolean
  ) {
    searchPatients(patient: $patient, isFuzzySearch: $isFuzzySearch) {
      uuid
      card {
        id
      }
      person {
        contactPoints {
          system
          value
        }
        name {
          fullname
        }
        addresses {
          country
          state
          street
          subCounty
          ward
        }        
      }
    }
  }
`;

gql`
  query SearchPatientsForRegistration($patient: SearchPatientInput!) {
    searchPatients(patient: $patient) {
      uuid
      person {
        dob
        name {
          firstname
          middlename
          lastname
        }
        contactPoints {
          system
          value
        }
        addresses {
          street
        }
        nationalIdNumber
      }
    }
  }
`;
